import type { GeocodeResult } from '@roadworld/shared';

const STORAGE_KEY = 'roadworld.savedLocations.v1';

export interface SavedLocation {
  lat: number;
  lng: number;
  /** Full resolved name from the geocoder, e.g. "Uppsala, Uppsala County, Sweden". */
  displayName: string;
  /** Short label for the dropdown — first comma-separated segment of displayName. */
  shortName: string;
  /** Epoch ms — used to sort newest-first in the dropdown. */
  savedAt: number;
}

export function loadAll(): SavedLocation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedLocation);
  } catch {
    return [];
  }
}

/** Add `loc` to the saved list. Dedupes on `displayName` — re-saving an existing entry
 *  bumps its `savedAt` so it floats back to the top. Returns the new list. */
export function save(loc: GeocodeResult): SavedLocation[] {
  const all = loadAll().filter((s) => s.displayName !== loc.displayName);
  all.unshift({
    lat: loc.lat,
    lng: loc.lng,
    displayName: loc.displayName,
    shortName: shortName(loc.displayName),
    savedAt: Date.now(),
  });
  persist(all);
  return all;
}

export function remove(displayName: string): SavedLocation[] {
  const all = loadAll().filter((s) => s.displayName !== displayName);
  persist(all);
  return all;
}

function persist(list: SavedLocation[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (err) {
    console.warn('[savedLocations] localStorage write failed', err);
  }
}

function shortName(displayName: string): string {
  return displayName.split(',')[0]?.trim() ?? displayName;
}

function isSavedLocation(v: unknown): v is SavedLocation {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.lat === 'number' &&
    typeof o.lng === 'number' &&
    typeof o.displayName === 'string' &&
    typeof o.shortName === 'string' &&
    typeof o.savedAt === 'number'
  );
}
